/**
 * runtimeFlags — DAM Dashboard Diagnostic Pack DD1.
 *
 * Single source of truth for reading the DAM-related Vite build-time
 * flags from the running bundle so the admin dashboard can surface
 * mode-state at a glance instead of forcing operators into DevTools.
 *
 * The flags themselves are written by `cloudbuild.yaml`'s
 * `_DAM_SHADOW_MODE` / `_DAM_LIVE_MODE` substitutions → Dockerfile
 * `ARG VITE_DAM_*` → Vite build env. `damLiveMode.ts:46-56` and
 * `shadowMode.ts:50-61` already read them at the service layer with
 * the same case-insensitive `'true'` semantics; this module mirrors
 * that pattern in a UI-facing form (booleans + a build-hash getter)
 * WITHOUT re-importing from those services to avoid a circular
 * `services/dam <-> lib` dependency.
 *
 * Why a separate helper rather than re-exporting from damLiveMode/
 * shadowMode: those services scope the flag to specific report
 * types (`LIVE_ENABLED_TYPES`, `SHADOW_ENABLED_TYPES`). The dashboard
 * needs the RAW bundle-state — is `_DAM_SHADOW_MODE=true` baked into
 * this build, yes or no — irrespective of any per-report-type
 * scoping. Folding the two would conflate "flag is on" with "flag
 * applies to this report type" and produce misleading chips when
 * Phase 4 widens the enabled types.
 */

declare const __BUILD_HASH__: string;
declare const __GENAI_BACKEND__: string;

/**
 * True iff the running bundle was built with
 * `VITE_DAM_SHADOW_MODE=true` (case-insensitive). Any other value —
 * including `undefined`, the empty string, or `'false'` — is falsy.
 *
 * In dev/test (Node + Vite), reads `process.env.VITE_DAM_SHADOW_MODE`
 * as a fallback so the same helper drives both runtime and tests.
 */
export function isDamShadowEnabled(): boolean {
  return readBoolFlag('VITE_DAM_SHADOW_MODE');
}

/**
 * True iff the running bundle was built with
 * `VITE_DAM_LIVE_MODE=true` (case-insensitive). Mirrors
 * `damLiveMode.ts:isDamLiveModeEnabled` but WITHOUT the per-report-
 * type scope — the dashboard wants raw bundle state.
 */
export function isDamLiveEnabled(): boolean {
  return readBoolFlag('VITE_DAM_LIVE_MODE');
}

/**
 * Returns the short git SHA stamped into the bundle at build time
 * via Vite's `define` block (`__BUILD_HASH__`). Falls back to
 * `'unknown'` if the global is unavailable (older Node / SSR / test).
 *
 * The full hash is short (~7 chars) by convention upstream
 * (`cloudbuild.yaml` passes `git rev-parse --short HEAD`), so no
 * slicing is required at the call site; consumers may still slice
 * for display.
 */
export function getBuildSha(): string {
  try {
    if (typeof __BUILD_HASH__ === 'string' && __BUILD_HASH__.length > 0) {
      return __BUILD_HASH__;
    }
  } catch { /* __BUILD_HASH__ undefined outside Vite — fall through */ }

  // Some tests assign `(globalThis as any).__BUILD_HASH__` to exercise
  // the success branch; honour it before giving up.
  try {
    const g = globalThis as unknown as { __BUILD_HASH__?: unknown };
    if (typeof g.__BUILD_HASH__ === 'string' && g.__BUILD_HASH__.length > 0) {
      return g.__BUILD_HASH__;
    }
  } catch { /* swallow */ }

  return 'unknown';
}

/**
 * Short human-readable description of the bundle's DAM state, used
 * by the Build SHA chip's hover tooltip.
 */
export function describeRuntimeEnv(): string {
  const shadow = isDamShadowEnabled() ? 'shadow=ON' : 'shadow=OFF';
  const live = isDamLiveEnabled() ? 'live=ON' : 'live=OFF';
  return `${shadow} · ${live}`;
}

/**
 * VR-1 — returns the GenAI backend the proxy is configured for at build
 * time (`'vertex'` or `'aistudio'`).
 */
export function getGenaiBackend(): 'vertex' | 'aistudio' {
  const resolve = (raw: string): 'vertex' | 'aistudio' =>
    raw === 'aistudio' ? 'aistudio' : 'vertex';

  // Honour globalThis first so Vitest (and dev-console overrides) can exercise
  // branches without rebuilding — mirrors getBuildSha().
  try {
    const g = globalThis as unknown as { __GENAI_BACKEND__?: unknown };
    if (typeof g.__GENAI_BACKEND__ === 'string') {
      return resolve(g.__GENAI_BACKEND__);
    }
  } catch { /* swallow */ }

  try {
    if (typeof __GENAI_BACKEND__ === 'string' && __GENAI_BACKEND__.length > 0) {
      return resolve(__GENAI_BACKEND__);
    }
  } catch { /* __GENAI_BACKEND__ undefined outside Vite — fall through */ }

  return 'vertex';
}

/**
 * SSM body content guard (row 0.99200 / backlog H-3).
 *
 * Adds an `[unverified inferred-from-context]` chip in front of `[Ref N]`
 * tokens that resolve to refs flagged "inferred-from-context" (no provider
 * URL + an inference sentinel in the source string). The chip is applied at
 * REPORT-GENERATION time (server-side, so it persists into the stored report)
 * by the post-gen audit pipeline — see `composePostGenAudit.ts`.
 *
 * True iff `SSM_BODY_GUARD=true` (the runtime Cloud Run Job env var read by
 * the bundled report-generation job via `process.env`) OR the build-time
 * `VITE_SSM_BODY_GUARD=true` (browser bundle). Either set to `'true'`
 * (case-insensitive) flips it on; any other value — including `undefined`,
 * the empty string, or `'false'` — keeps it OFF (the prod-default state).
 *
 * Mirrors the dual runtime/build precedence of `isEoAnnualYieldEnabled`.
 * DEFAULT OFF until calibrated; with the flag OFF the guard is byte-identical
 * no-op (no token is ever rewritten).
 */
export function isSsmBodyGuardEnabled(): boolean {
  return readBoolFlag('SSM_BODY_GUARD') || readBoolFlag('VITE_SSM_BODY_GUARD');
}

/**
 * CMU unified evidence panel — when true, Extracted Variables live
 * inside the Coverage Matrix requirement panel; the separate tab is
 * demoted/hidden. Default off until operators flip the build flag.
 */
export function isUnifiedEvidencePanelEnabled(): boolean {
  return readBoolFlag('VITE_UNIFIED_EVIDENCE_PANEL');
}

/**
 * Page-level ranked document match — the single Gemini-scored
 * `matchUploadedDocsToChecklistRanked` run `useSiteCoverageMatch` starts on
 * a site page load (605734660), feeding Path to GO and the Site Evidence tab.
 *
 * DEFAULT ON. Only an explicit `PAGE_COVERAGE_MATCH=false` (Node / tests) or
 * `VITE_PAGE_COVERAGE_MATCH=false` (build-time; `0` / `off` also count) holds
 * the run. The hermetic generation e2e sets it: that run gives the app no
 * Gemini key and its fixture fails on any `/api/gemini-proxy` request, and
 * before 605734660 the match only ran when the Site Evidence tab was opened,
 * which that e2e never does. Production and development builds never set the
 * variable, so their behaviour is unchanged.
 */
export function isPageCoverageMatchEnabled(): boolean {
  const raw = [readEnvFlag('PAGE_COVERAGE_MATCH'), readEnvFlag('VITE_PAGE_COVERAGE_MATCH')]
    .find(value => typeof value === 'string' && value.trim() !== '');
  return !(typeof raw === 'string' && ['false', '0', 'off'].includes(raw.trim().toLowerCase()));
}

/**
 * Intent Intelligence — next-best-input nudge (Slice 2/5).
 *
 * Gates whether the Project-Intent form re-surfaces the "Suggested next"
 * nudge — the single highest-value undecided field the user should fill in
 * next (`useIntentIntelligence().nextBestInput`, ranked by
 * `rankNextInputs`). The per-field suggestion CHIPS are unaffected by this
 * flag (they ship unconditionally); this flag governs ONLY the aggregated
 * top-of-form nudge that was decluttered away on 2026-06-10.
 *
 * DEFAULT OFF (dev/test AND prod) until a human flips it. With the flag OFF
 * the form is byte-identical to today: the `nextBestInput` prop is consumed
 * inertly (no nudge element is ever rendered), exactly as the post-declutter
 * `void nextBestInput;` path behaves. The nudge NEVER asserts an unverified
 * value — it only offers a value when the deterministic suggester produced a
 * non-`fallback`, context-grounded suggestion for that field, and accepting
 * routes through the SAME writer + `fieldSource='accepted'` stamp as the
 * per-field chip, so a user-authored field is never clobbered or re-nudged.
 *
 * Plumbed through `cloudbuild.yaml` `_INTENT_NEXT_BEST_INPUT` → Dockerfile
 * `ARG VITE_INTENT_NEXT_BEST_INPUT` → Vite build env, mirroring the
 * documented `VITE_DASHBOARD_V2` / `VITE_SSM_BODY_GUARD` chains. Reads either
 * the runtime `INTENT_NEXT_BEST_INPUT` (Node / tests) or the build-time
 * `VITE_INTENT_NEXT_BEST_INPUT`; either set to `'true'` (case-insensitive)
 * flips it on. Any other value — including `undefined`, the empty string, or
 * `'false'` — keeps it OFF (the prod-default state).
 */
export function isIntentNextBestInputEnabled(): boolean {
  return readBoolFlag('INTENT_NEXT_BEST_INPUT') || readBoolFlag('VITE_INTENT_NEXT_BEST_INPUT');
}

/**
 * Cover placement debug overlay gate.
 *
 * Enabled only when the browser URL includes `?coverDebug=1`.
 * Never enabled in non-browser contexts.
 */
export function isCoverDebugEnabled(): boolean {
  if (typeof window === 'undefined' || !window.location) return false;
  try {
    return new URLSearchParams(window.location.search).get('coverDebug') === '1';
  } catch {
    return false;
  }
}

/**
 * Dashboard v2 rollout flag.
 *
 * Default behavior:
 * - dev/test: ON (unless explicitly set false)
 * - prod: OFF (unless explicitly set true)
 */
export function isDashboardV2Enabled(): boolean {
  const explicit = readEnvFlag('VITE_DASHBOARD_V2');
  if (typeof explicit === 'string' && explicit.trim() !== '') {
    return explicit.trim().toLowerCase() === 'true';
  }
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, unknown> }).env) {
      const env = (import.meta as { env: Record<string, unknown> }).env;
      return env.PROD !== true;
    }
  } catch {
    /* no-op */
  }
  return process.env.NODE_ENV !== 'production';
}

/**
 * Parcel Test-Fit Studio workstation rollout.
 *
 * The correctness fixes are unconditional; this flag controls only the new
 * canvas-first shell and camera chrome. Development and test builds default
 * ON, while production requires an explicit build-time opt-in.
 */
export function isParcelStudioV2Enabled(): boolean {
  const explicit = readEnvFlag('VITE_PARCEL_STUDIO_V2');
  if (typeof explicit === 'string' && explicit.trim() !== '') {
    return explicit.trim().toLowerCase() === 'true';
  }
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, unknown> }).env) {
      const env = (import.meta as { env: Record<string, unknown> }).env;
      return env.PROD !== true;
    }
  } catch {
    /* no-op */
  }
  return process.env.NODE_ENV !== 'production';
}

/**
 * Path-to-GO default-tab flag — RETIRED (Path to GO removal, stage 2).
 *
 * It used to land a new or coordinate-first site on the Path to GO hub
 * instead of Home. The hub tab is retired and every site now lands on Home
 * (owner decision), so the default is OFF in every environment — dev and
 * test included — and no landing code reads it any more. An explicit
 * `VITE_PATH_TO_GO_DEFAULT=true` is still parsed so an old build config does
 * not error, but it changes nothing. The tab code itself was deleted in stage
 * 3. The flag is kept, inert, because its build plumbing is part of the
 * deploy config: removing it re-indexes cloudbuild.yaml's positional build
 * arguments (pinned by cloudbuildManifests.test.ts), so it goes in its own
 * deploy-config change rather than riding on a UI deletion.
 */
export function isPathToGoDefaultTabEnabled(): boolean {
  const explicit = readEnvFlag('VITE_PATH_TO_GO_DEFAULT');
  return typeof explicit === 'string' && explicit.trim().toLowerCase() === 'true';
}



/**
 * Report grounding rollout flag (Report-gen quality remediation P1 —
 * tidy-sniffing-teacup rev9, "grounded content" half).
 *
 * Gates the CanonicalFactStore → `sedToCanonicalText` → blocking grounding
 * validator content layer. This governs WHAT the report SAYS (the prompt's
 * authoritative fact block + the post-gen anti-hallucination gate).
 *
 * When OFF (the prod-default state) NONE of the grounding modules are
 * constructed or called — generation reads `sedToText` exactly as today, so
 * the prompt, the report, and the `qaFindings` are byte-identical. The whole
 * layer is lazy-imported behind `reportGroundingMode() === 'off'` guards so
 * an OFF bundle does not even pull the code.
 *
 * Default behavior (mirrors `isDashboardV2Enabled`):
 * - dev/test: ON (unless explicitly set false)
 * - prod: OFF (unless explicitly set true)
 *
 * Plumbed through `cloudbuild.yaml` `_REPORT_GROUNDING` → Dockerfile
 * `ARG VITE_REPORT_GROUNDING` → Vite build env, mirroring the documented
 * `_DASHBOARD_V2` / `_EDITORIAL_NARRATIVE` chains. The server
 * report-generation JOB reads `process.env.REPORT_GROUNDING` (Cloud Run Job
 * env, CJS — NOT the baked VITE flag); the browser bundle reads the build-time
 * `VITE_REPORT_GROUNDING`. `readEnvFlag` falls back to `process.env`, so
 * setting EITHER `REPORT_GROUNDING` or `VITE_REPORT_GROUNDING` to a non-empty
 * `'true'`/`'shadow'`/`'warn'`/`'on'` value engages the corresponding mode.
 */
export function isReportGroundingEnabled(): boolean {
  const explicit = readEnvFlag('REPORT_GROUNDING') ?? readEnvFlag('VITE_REPORT_GROUNDING');
  if (typeof explicit === 'string' && explicit.trim() !== '') {
    const v = explicit.trim().toLowerCase();
    // Any of the staged "on-ish" modes (true / shadow builds the store /
    // warn / on) counts as enabled; only an explicit 'false'/'off' (or an
    // unrecognised token) reads as disabled.
    return v === 'true' || v === 'shadow' || v === 'warn' || v === 'on';
  }
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, unknown> }).env) {
      const env = (import.meta as { env: Record<string, unknown> }).env;
      return env.PROD !== true;
    }
  } catch {
    /* no-op */
  }
  return process.env.NODE_ENV !== 'production';
}

/**
 * Three-state rollout mode for `VITE_REPORT_GROUNDING` (shadow → warn → on),
 * so the content layer can be soaked before it blocks generation:
 *
 *  - `'off'`    — no grounding module is constructed; `sedToText` drives the
 *                 prompt; no new gate runs. Byte-identical to today.
 *  - `'shadow'` — the CanonicalFactStore is built + telemetry is logged, but
 *                 the LIVE `sedToText` prompt is left UNCHANGED and the
 *                 validator never blocks/repairs. Pure measurement.
 *  - `'warn'`   — `sedToCanonicalText` drives the prompt; violations are
 *                 logged + surfaced as non-blocking findings; never blocks.
 *  - `'on'`     — `sedToCanonicalText` drives the prompt; blocking violations
 *                 trigger the one-shot grounding repair, then are recorded.
 *
 * The explicit token wins. A bare `'true'` maps to `'on'`. When no explicit
 * value is set, the mode follows `isReportGroundingEnabled()` (dev/test →
 * `'on'`, prod → `'off'`), so a default dev build exercises the full path.
 */
export type GroundingMode = 'off' | 'shadow' | 'warn' | 'on';
export function reportGroundingMode(): GroundingMode {
  const raw = (readEnvFlag('REPORT_GROUNDING') ?? readEnvFlag('VITE_REPORT_GROUNDING') ?? '')
    .trim()
    .toLowerCase();
  if (raw === 'shadow' || raw === 'warn' || raw === 'on') return raw;
  if (raw === 'true') return 'on';
  if (raw === 'false' || raw === 'off') return 'off';
  return isReportGroundingEnabled() ? 'on' : 'off';
}

/**
 * Anti-AI-voice ENFORCEMENT mode (P2-GOLD P4 / Part A3).
 *
 * Gates whether accumulated anti-AI-voice findings (R-132) can ESCALATE to a
 * blocking severity + a one-shot regenerate pass (mirroring the grounding
 * gate), or stay advisory `'warn'` findings. This governs ONLY enforcement —
 * the DETECTOR itself (coverage + pattern tightening, Part A1/A2) is always-on
 * and unaffected by this flag.
 *
 *  - `'warn'`  — (DEFAULT, prod-default) findings are surfaced in the QA audit
 *    only; nothing blocks and nothing regenerates. BYTE-IDENTICAL to today's
 *    behaviour for generation: the voice findings are advisory.
 *  - `'on'` / `'block'` — when the accumulated voice findings cross the
 *    configured threshold, the wire layer escalates to a SINGLE regenerate
 *    pass (guarded by `_voiceRepairUsed`). If the regenerated draft still
 *    trips, it FALLS BACK to warn-level findings and PUBLISHES — enforcement
 *    can never hard-fail/throw/block a generation.
 *
 * DEFAULT: `'warn'` (enforcement OFF) in dev/test AND prod, so the prod
 * generation pipeline is unchanged until a human flips it. Reads the runtime
 * `REPORT_VOICE_ENFORCE` (Cloud Run Job env, CJS) OR the build-time
 * `VITE_REPORT_VOICE_ENFORCE`. An explicit `'on'`/`'block'`/`'true'` engages
 * enforcement; any other value — including `undefined`, the empty string,
 * `'warn'`, `'off'`, or `'false'` — keeps it in the safe warn-only state.
 *
 * Plumbed (when enabled for a build) through `cloudbuild.yaml`
 * `_REPORT_VOICE_ENFORCE` → Dockerfile `ARG VITE_REPORT_VOICE_ENFORCE` → Vite
 * build env, mirroring the documented `_REPORT_GROUNDING` chain.
 */
export type VoiceEnforceMode = 'warn' | 'on';
export function reportVoiceEnforceMode(): VoiceEnforceMode {
  const raw = (readEnvFlag('REPORT_VOICE_ENFORCE') ?? readEnvFlag('VITE_REPORT_VOICE_ENFORCE') ?? '')
    .trim()
    .toLowerCase();
  if (raw === 'on' || raw === 'block' || raw === 'true') return 'on';
  // Everything else (incl. '', 'warn', 'off', 'false', unknown) → warn (OFF).
  return 'warn';
}

/**
 * EO-native annual-yield compatibility flag.
 *
 * Explicit `false` disables EO annual-yield consumers in environments that
 * still need a rollback switch. Unset/default is enabled: active annual-yield
 * runs are EO-sourced and retired BoP execution documents are not candidates.
 *
 * Plumbed through `cloudbuild.yaml` `_EO_ANNUAL_YIELD` → Dockerfile
 * `ARG VITE_EO_ANNUAL_YIELD` → Vite build env. The server
 * reads it via `process.env.EO_ANNUAL_YIELD` (Cloud Run Job env) OR the
 * `VITE_EO_ANNUAL_YIELD` build flag. An explicit non-empty value enables the
 * flag unless it is `'false'` (case-insensitive); undefined/empty defaults on.
 */
export function isEoAnnualYieldEnabled(): boolean {
  const explicit = readEnvFlag('EO_ANNUAL_YIELD') ?? readEnvFlag('VITE_EO_ANNUAL_YIELD');
  if (typeof explicit === 'string' && explicit.trim() !== '') {
    return explicit.trim().toLowerCase() !== 'false';
  }
  return true; // default flip
}

/**
 * C0-GEN — Editorial-narrative generation (Option B).
 *
 * Gates whether the generated editorial-narrative layer (flowing monograph
 * prose re-voicing already-verified section content) is produced and
 * rendered. DEFAULT OFF: when OFF (the prod-default state) no
 * `editorialNarrative` field is ever written, so the resolver always
 * returns the deterministic Option-A fallback and the report renders
 * byte-identically to today.
 *
 * Mirrors the dual runtime/build precedence of `isSsmBodyGuardEnabled` /
 * `isEoAnnualYieldEnabled`: the server (report-generation job) reads
 * `EDITORIAL_NARRATIVE` via `process.env`; the browser bundle reads the
 * build-time `VITE_EDITORIAL_NARRATIVE`. Either set to `'true'` (case-
 * insensitive) flips it on; any other value — including `undefined`, the
 * empty string, or `'false'` — keeps it OFF.
 *
 * Plumbed through `cloudbuild.yaml` `_EDITORIAL_NARRATIVE` → Dockerfile
 * `ARG VITE_EDITORIAL_NARRATIVE` → Vite build env, mirroring the documented
 * `_DAM_*` / `_EO_ANNUAL_YIELD` / `_SSM_BODY_GUARD` chains.
 */
export function isEditorialNarrativeEnabled(): boolean {
  return readBoolFlag('EDITORIAL_NARRATIVE') || readBoolFlag('VITE_EDITORIAL_NARRATIVE');
}

/**
 * AI LAYOUT/DESIGN PASS (report-ai-design-pass · Phase 2) — generation-step gate.
 *
 * Gates whether the bounded AI LAYOUT/DESIGN pass runs after a report's content
 * is grounded / fact-checked / QA'd. When ON, a rule-constrained Gemini call
 * emits a `ReportLayoutPlan` of DESIGN DECISIONS (cover treatment, per-section
 * columns / flow / page-breaks / exhibit placement / editorial emphasis) that
 * the engine later applies + validates (Phases 3–4). The plan carries NO prose,
 * numbers, or citations — the schema is structurally incapable of mutating
 * content.
 *
 * DEFAULT OFF (the prod-default state): when OFF the pass is NEVER called, so no
 * `layoutPlan` is generated or written, every consuming resolver returns today's
 * deterministic value, and the report renders BYTE-IDENTICALLY to today. The
 * pass is purely additive — an absent plan changes nothing.
 *
 * Mirrors the dual runtime/build precedence of `isEditorialNarrativeEnabled`:
 * the server (report-generation job) reads `REPORT_LAYOUT_PASS` via
 * `process.env`; the browser bundle reads the build-time
 * `VITE_REPORT_LAYOUT_PASS`. Either set to `'true'` (case-insensitive) flips it
 * on; any other value — including `undefined`, the empty string, or `'false'` —
 * keeps it OFF. Plumb through `cloudbuild.yaml` `_REPORT_LAYOUT_PASS` →
 * Dockerfile `ARG VITE_REPORT_LAYOUT_PASS` → Vite build env when a build-time
 * rollout is wanted (mirrors the `_EDITORIAL_NARRATIVE` chain).
 */
export function isReportLayoutPassEnabled(): boolean {
  return readBoolFlag('REPORT_LAYOUT_PASS') || readBoolFlag('VITE_REPORT_LAYOUT_PASS');
}

/**
 * Editorial-Feature Monograph layout rollout flag (Part L — editorial-layouts-l1).
 *
 * Gates the NEW editorial-monograph direction — the `editorial` theme preset,
 * the `editorial-masthead` title page, the per-section `EditorialSectionLayout`
 * opener, and the scoped pull-quote / drop-cap treatment. The editorial
 * primitives are additive and inert on their own; this single flag is the ONE
 * switch the renderer reads to decide whether to mount any of them.
 *
 * Build-time only (baked into the bundle — VITE_ vars cannot be toggled at
 * runtime). Plumbed through `cloudbuild.yaml` `_EDITORIAL_LAYOUTS` → Dockerfile
 * `ARG VITE_EDITORIAL_LAYOUTS` → Vite build env.
 *
 * Default behaviour: FALSE in dev/test AND prod (uses the strict `readBoolFlag`
 * rule). The
 * editorial treatment must be BYTE-STABLE OFF everywhere by default — existing
 * reports, covers, presets, and the 66-cell visual matrix render identically —
 * so the flag stays OFF until a human flips it for preview. Set
 * `VITE_EDITORIAL_LAYOUTS=true` (case-insensitive) to turn it on; any other
 * value — including `undefined`, the empty string, or `'false'` — keeps it OFF.
 */
export function isEditorialLayoutsEnabled(): boolean {
  return readBoolFlag('VITE_EDITORIAL_LAYOUTS');
}

/**
 * Part L2 — the editorial TWO-COLUMN justified body sub-flag.
 *
 * The 2-col body is the load-bearing RISK of the editorial direction (the 2-col
 * preset family was retired 2026-06-09 because pagedjs 0.4.3 cannot reliably
 * fragment multicol on the measured deck). It is therefore a SEPARATE switch
 * NESTED under `isEditorialLayoutsEnabled()`: editorial structure (openers,
 * standfirsts, pull-quotes, the Datum-Ledger cover, dark dividers) ships as the
 * 1-col default; the 2-col body engages ONLY when the editorial flag is ON AND
 * this sub-flag is explicitly ON. This gives the documented FALLBACK CONTRACT a
 * clean lever: if the per-page reflow cannot be made reliable across all 11
 * types (the `--live` gate catches overlap/blank/crush), 2-col stays OFF and the
 * editorial default is a stable 1-col — without disabling the editorial
 * structure. Build-time only, mirroring the other VITE_ chains.
 *
 * Default: FALSE everywhere (1-col editorial). Set `VITE_EDITORIAL_2COL=true`
 * (case-insensitive) to engage the 2-col body for verification.
 */
export function isEditorial2ColEnabled(): boolean {
  return isEditorialLayoutsEnabled() && readBoolFlag('VITE_EDITORIAL_2COL');
}

/**
 * Report Themes v2 rollout flag (3 new theme presets + their title pages).
 *
 * Gates the NEW report-theme directions added alongside the editorial monograph:
 * the `prospectus` / `press` / `signal` theme presets and their matching
 * `prospectus` / `frontispiece` / `title-block` cover title pages, plus the
 * five new self-hosted display faces (Fraunces / Source Serif 4 / EB Garamond /
 * Archivo / Hanken Grotesk) those presets select. Mirrors
 * `isEditorialLayoutsEnabled`: the presets + covers are additive and inert on
 * their own; this single flag is the ONE switch the Studio pickers read to decide
 * whether to OFFER them, and (because the presets are kept OUT of the 6-cell
 * matrix and never default) nothing resolves to a v2 cover/preset unless a human
 * selects one.
 *
 * Build-time only (baked into the bundle — VITE_ vars cannot be toggled at
 * runtime). Plumbed through `cloudbuild.yaml` `_REPORT_THEMES_V2` → Dockerfile
 * `ARG VITE_REPORT_THEMES_V2` → Vite build env, mirroring the documented
 * `_EDITORIAL_LAYOUTS` chain.
 *
 * Default behaviour: FALSE in dev/test AND prod (strict `readBoolFlag`). The v2 themes must be
 * BYTE-STABLE OFF everywhere by default — existing reports, covers, presets, and
 * the 66-cell visual matrix render identically — so the flag stays OFF until a
 * human flips it for preview. Set `VITE_REPORT_THEMES_V2=true` (case-insensitive)
 * to turn it on; any other value — including `undefined`, the empty string, or
 * `'false'` — keeps it OFF.
 */
export function isReportThemesV2Enabled(): boolean {
  return readBoolFlag('VITE_REPORT_THEMES_V2');
}

/**
 * Report-generation V2 stage flags are deliberately independent. The runtime
 * names control the API/job/server surfaces; VITE_ counterparts are available
 * to browser code and local tests. All default OFF and accept only `true`.
 * Downstream stages still enforce their data prerequisites (for example, the
 * artifact pipeline cannot run without the document-model stage).
 */
function reportStageFlag(runtimeName: string, buildName: string): boolean {
  return readBoolFlag(runtimeName) || readBoolFlag(buildName);
}

/** Require an explicit schemaVersion=2 enqueue envelope. OFF keeps normalization for legacy clients. */
export function isReportRequestV2Enabled(): boolean {
  return reportStageFlag('REPORT_REQUEST_V2', 'VITE_REPORT_REQUEST_V2');
}

/** Prerequisite gate for immutable ReportDocumentModelV1 finalization. */
export function isReportDocumentV1Enabled(): boolean {
  return reportStageFlag('REPORT_DOCUMENT_V1', 'VITE_REPORT_DOCUMENT_V1');
}

/** Build, validate and atomically expose immutable delivery artifacts. */
export function isReportArtifactPipelineV2RolloutEnabled(): boolean {
  return reportStageFlag('REPORT_ARTIFACT_PIPELINE_V2', 'VITE_REPORT_ARTIFACT_PIPELINE_V2');
}

/**
 * Fixed-page PDF.js viewer rollout. This is primarily a Vite build flag because
 * the client bundle cannot be changed by a Cloud Run runtime env update.
 */
export function isReportFixedViewerV1Enabled(): boolean {
  return reportStageFlag('REPORT_FIXED_VIEWER_V1', 'VITE_REPORT_FIXED_VIEWER_V1')
    // Compatibility alias for early V2 worktrees; remove only after manifests
    // and cached builds no longer reference it.
    || reportStageFlag('REPORT_ARTIFACT_VIEWER_V2', 'VITE_REPORT_ARTIFACT_VIEWER_V2');
}

/** Build/stream sanitized immutable public revisions. Server-runtime by default. */
export function isReportPublicProjectionV2Enabled(): boolean {
  return reportStageFlag('REPORT_PUBLIC_PROJECTION_V2', 'VITE_REPORT_PUBLIC_PROJECTION_V2');
}

/**
 * Web-first decision-analysis rollout.
 *
 * The server mode is intentionally ordered so deployments can first persist
 * semantic revisions, then read them in the Findings UI, and only finally
 * move the positive-decision gate away from PDF delivery. Unknown values fail
 * closed to `off`.
 */
export type DecisionAnalysisV1Mode = 'off' | 'shadow' | 'read' | 'gate';

export function getDecisionAnalysisV1Mode(): DecisionAnalysisV1Mode {
  const raw = readEnvFlag('DECISION_ANALYSIS_V1_MODE')
    ?? readEnvFlag('VITE_DECISION_ANALYSIS_V1_MODE');
  const normalized = raw?.trim().toLowerCase();
  return normalized === 'shadow' || normalized === 'read' || normalized === 'gate'
    ? normalized
    : 'off';
}

/** Persist semantic analysis revisions before any optional presentation work. */
export function isDecisionAnalysisPersistenceEnabled(): boolean {
  return getDecisionAnalysisV1Mode() !== 'off';
}

/** Use analysis readiness, rather than PDF delivery, for positive decisions. */
export function isDecisionAnalysisGateEnabled(): boolean {
  return getDecisionAnalysisV1Mode() === 'gate';
}

/**
 * Findings is a build-time UI surface. It may be exercised independently in
 * development while the server remains in shadow mode, but production still
 * requires an explicit Vite flag.
 */
export function isDecisionFindingsV1Enabled(): boolean {
  const explicit = readEnvFlag('VITE_DECISION_FINDINGS_V1');
  if (typeof explicit === 'string' && explicit.trim() !== '') {
    return explicit.trim().toLowerCase() === 'true';
  }
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, unknown> }).env) {
      return (import.meta as { env: Record<string, unknown> }).env.PROD !== true;
    }
  } catch {
    /* no-op */
  }
  return process.env.NODE_ENV !== 'production';
}

/**
 * Call-ready Findings is an additive, build-time UI composition. Development
 * defaults ON so a clean checkout exposes the released workflow; production is
 * controlled by the explicit Cloud Build flag and can be rolled back with
 * `false`.
 */
export function isCallReadyFindingsV1Enabled(): boolean {
  return readBoolFlagWithDevelopmentDefault('VITE_CALL_READY_FINDINGS_V1');
}

/**
 * Community & Economic Impact cards are independently releasable from the
 * call-ready shell. Development defaults ON; production remains an explicit
 * build-time choice. Evidence and source-policy checks still fail closed.
 */
export function isEconomicContributionV1Enabled(): boolean {
  return readBoolFlagWithDevelopmentDefault('VITE_ECONOMIC_CONTRIBUTION_V1');
}

/** Backward-compatible consumer name used by SiteRagEngine. */
export function isReportArtifactViewerEnabled(): boolean {
  return isReportFixedViewerV1Enabled();
}

/**
 * BOM → Simulation generation UI gate (Owner request, 2026-11-02).
 *
 * Temporarily hides the "Generate Simulation" action in the BOM tool
 * (`SupplyChainBomPanel` / `SupplyChainPanel`) and the "+ Generate from BOM"
 * buttons in Engineering Analysis (`SiteLandingPage`) while the BOM-to-sim
 * generation flow is being repaired, without deleting the underlying
 * `GenerateSimulationWizard` implementation, stored BOMs, existing
 * simulations, or adjacent simulation-editor / Open controls.
 *
 * DEFAULT OFF (`false`) in all environments until `BOM_SIMULATION_GENERATION=true`
 * or `VITE_BOM_SIMULATION_GENERATION=true` is explicitly enabled after the
 * flow is fixed and verified.
 */
export function isBomSimulationGenerationEnabled(): boolean {
  return readBoolFlag('BOM_SIMULATION_GENERATION') || readBoolFlag('VITE_BOM_SIMULATION_GENERATION');
}

/**
 * Read an env flag with the case-insensitive `'true'` rule used
 * everywhere else in the DAM pipeline. Returns false for any other
 * value, including `undefined` (the production-default state).
 */
function readBoolFlag(name: string): boolean {
  const raw = readEnvFlag(name);
  return typeof raw === 'string' && raw.trim().toLowerCase() === 'true';
}

/**
 * Released UI surfaces should be visible in a clean local checkout while
 * remaining fail-closed in a production bundle whose build flag is absent.
 * An explicit value always wins, so `false` remains a deterministic rollback.
 */
function readBoolFlagWithDevelopmentDefault(name: string): boolean {
  const explicit = readEnvFlag(name);
  if (typeof explicit === 'string' && explicit.trim() !== '') {
    return explicit.trim().toLowerCase() === 'true';
  }
  if (typeof process !== 'undefined'
    && process.env
    && (process.env.NODE_ENV === 'test' || process.env.VITEST === 'true')) {
    return false;
  }
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, unknown> }).env) {
      return (import.meta as { env: Record<string, unknown> }).env.PROD !== true;
    }
  } catch {
    /* no-op */
  }
  return process.env.NODE_ENV !== 'production';
}

/**
 * Mirror of `damLiveMode.ts:readEnvFlag` so this module stays
 * service-free. Prefers `import.meta.env` (the browser bundle path)
 * and falls back to `process.env` (Node / tests / CLI).
 */
function readEnvFlag(name: string): string | undefined {
  // Vite bakes computed-key import.meta.env reads into each transformed module,
  // so a developer's release-like .env.local can otherwise leak into Vitest and
  // defeat per-test flag setup. Test runners own their environment explicitly;
  // production and development bundles retain the import.meta-first precedence.
  if (typeof process !== 'undefined'
    && process.env
    && (process.env.VITEST === 'true' || process.env.NODE_ENV === 'test')) {
    return process.env[name];
  }
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as { env?: unknown }).env != null) {
      const v = ((import.meta as { env: Record<string, unknown> }).env)[name];
      if (typeof v === 'string') return v;
    }
  } catch { /* import.meta unavailable in older Node */ }
  if (typeof process !== 'undefined' && process.env) {
    return process.env[name];
  }
  return undefined;
}

export type ReportFinalRepairMode = 'off' | 'shadow' | 'deterministic';
/** No model phase is shipped. The unqualified default remains measurement-only. */
export function reportFinalRepairMode(): ReportFinalRepairMode {
  const raw = (readEnvFlag('REPORT_FINAL_REPAIR') ?? readEnvFlag('VITE_REPORT_FINAL_REPAIR'))?.trim().toLowerCase();
  if (raw === 'off' || raw === 'deterministic') return raw;
  // 'on' is deliberately not an alias for an unapproved model/activation mode.
  return 'shadow';
}
